import { SERVER_URL } from '../../js/config.js';
import { createRetryableHttpError, isTransientHttpStatus, shouldRetryMethod, withRetry } from '../../js/utils/error-handling.js';

class ApiClient {
  constructor() {
    this.pendingRequests = new Map();
    this.cache = new Map();
    this.cacheTTL = 5 * 60 * 1000;
    this.baseUrl = `${SERVER_URL}/api`;
    this.isInitialized = false;
    this.initPromise = null;
  }

  async init() {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise(resolve => {
      this.isInitialized = true;
      resolve();
    });

    return this.initPromise;
  }

  setBaseUrl(url) {
    this.baseUrl = url;
  }

  async get(endpoint, options = {}) {
    await this._ensureInit();
    
    const cacheKey = `GET:${endpoint}:${JSON.stringify(options)}`;
    
    if (!options.forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached?.expiry > Date.now()) return cached.data;
    }
    
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }
    
    const request = this._makeRequest('GET', endpoint, options);
    this.pendingRequests.set(cacheKey, request);
    
    try {
      const data = await request;
      this.cache.set(cacheKey, { data, expiry: Date.now() + this.cacheTTL });
      return data;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  async post(endpoint, data, options = {}) {
    await this._ensureInit();
    return this._makeRequest('POST', endpoint, { ...options, body: data });
  }

  async put(endpoint, data, options = {}) {
    await this._ensureInit();
    return this._makeRequest('PUT', endpoint, { ...options, body: data });
  }

  async delete(endpoint, options = {}) {
    await this._ensureInit();
    return this._makeRequest('DELETE', endpoint, options);
  }

  _buildUrl(endpoint) {
    if (endpoint.startsWith('http')) return endpoint;
    if (!endpoint.startsWith('/')) endpoint = '/' + endpoint;
    return `${SERVER_URL}/api${endpoint}`;
  }

  _buildHeaders(options = {}) {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    };
  }

  async _ensureInit() {
    if (!this.isInitialized) await this.init();
  }

  async _makeRequest(method, endpoint, options = {}) {
    const url = this._buildUrl(endpoint);
    const upperMethod = String(method || 'GET').toUpperCase();
    const {
      allowUnsafeRetry = false,
      onRetryEvent = null,
      ...requestOptions
    } = options;
    const resolvedRequestOptions = /** @type {any} */ (requestOptions);
    
    try {
      const requestOperation = async () => {
        const requestBody = resolvedRequestOptions.body ? JSON.stringify(resolvedRequestOptions.body) : undefined;
        const fetchOptions = {
          method: upperMethod,
          mode: /** @type {RequestMode} */ ('cors'),
          ...(requestBody ? { body: requestBody } : {}),
          ...(!window.tokenManager && { headers: this._buildHeaders(resolvedRequestOptions) })
        };

        if (window.tokenManager && resolvedRequestOptions.headers) {
          fetchOptions.headers = resolvedRequestOptions.headers;
        }

        const response = window.tokenManager
          ? await window.tokenManager.authenticatedFetch(url, {
            ...fetchOptions,
            _allowUnsafeRetry: allowUnsafeRetry,
            _retryTelemetry: onRetryEvent
          })
          : await fetch(url, fetchOptions);

        if (!window.tokenManager && isTransientHttpStatus(response.status) && shouldRetryMethod(upperMethod, allowUnsafeRetry)) {
          throw createRetryableHttpError(response, { endpoint: url, method: upperMethod });
        }

        return response;
      };

      const response = await withRetry(
        requestOperation,
        {
          maxRetries: window.tokenManager ? 0 : undefined,
          method: upperMethod,
          endpoint: url,
          allowUnsafeMethods: allowUnsafeRetry,
          onRetryEvent
        },
        { label: 'apiClient._makeRequest', endpoint: url, method: upperMethod }
      );

      if (!response.ok) {
        const baseError = `API error: ${response.status} ${response.statusText}`;
        let detailMessage = '';
        try {
          const errorData = await response.json();
          detailMessage = errorData?.message || errorData?.error || '';
        } catch {
          detailMessage = '';
        }

        const requestError = /** @type {any} */ (new Error(detailMessage ? `${baseError} - ${detailMessage}` : baseError));
        requestError.status = response.status;
        requestError.endpoint = endpoint;
        requestError.url = url;
        throw requestError;
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      if (error?.message?.includes('NetworkError') ||
          error?.message?.includes('Failed to fetch') ||
          error?.message?.includes('Network request failed')) {
        throw new Error(`Network error: Could not connect to ${url}`);
      }
      throw error;
    }
  }
}

export const apiClient = new ApiClient();
